import {
  AfterViewInit,
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { of } from 'rxjs';
import { Store } from '@ngxs/store';
import { delay } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import {
  AuthorType,
  ChatMediaKind,
  ChatServiceType,
  IChatMessage,
  IChatMessageMedia,
  INITIAL_MESSAGES_LIMIT_MAPPER,
} from '@nimbus-global/features/chat/models/chat.model';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { MediaBlockReason, TwilioMessageStatus } from '@nimbus-global/features/chat/models/chat-twilio.model';
import { ChatMessageStatus } from '@nimbus-global/features/chat/models/chat-service.model';
import { UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { getChatAttachmentIcon } from '@nimbus-global/features/chat/utils/chat-attachment-icon.util';
import { getMediaBlockReasonMessage } from '@nimbus-global/features/chat/utils/chat-media-block-reason.util';
import { getMessageDeliveryFailureReasonMessage } from '@nimbus-global/features/chat/utils/chat-message-failure-reason.util';
import { FileService } from '@nimbus-library/features/shared/services/helper/file.service';
import { IChatInlineNode } from './chat-format.pipe';

@UntilDestroy()
@Component({
  selector: 'nimbus-chat-messages',
  standalone: false,
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input()
  public messages: IChatMessage[] = [];
  @Input()
  public chatType: ChatServiceType;
  @Input()
  public taskState: UserInteractionOutcome;
  @Input()
  public emptyTemplate: TemplateRef<unknown>;
  @Input()
  public authorInfoTemplate: TemplateRef<IChatMessage>;
  @Input()
  public messageExtensionTemplate: TemplateRef<{ message: IChatMessage }>;
  @Input()
  public messageActionTemplate: TemplateRef<{ message: IChatMessage }>;
  @Input()
  public showActions = false;
  @Input()
  public hideAuthorTime = false;
  @Input()
  public hasMoreMessages = false;

  @Output()
  public copyTextInitialized = new EventEmitter<string>();
  @Output()
  public loadPrevMessagesRequested = new EventEmitter<void>();
  @Output()
  public messageSent = new EventEmitter<string>();

  private readonly mediaErrorKeys = new Set<string>();
  private readonly scrollBottomThreshold = 50;
  private isNearBottom = true;
  // Ignores echoes of our own scrollToBottom() write (same scrollTop as last observed), so a delayed echo
  // racing with media growth can't wrongly flip isNearBottom to false.
  private lastObservedScrollTop = 0;
  private containerRef: ElementRef<HTMLDivElement>;

  public readonly TranslationKeys = TranslationKeys;
  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;
  public readonly expandMessageLength = 770;
  public readonly AuthorType = AuthorType;
  public readonly ChatMediaKind = ChatMediaKind;
  public readonly MediaBlockReason = MediaBlockReason;
  public readonly copyableAuthorTypes: AuthorType[] = [AuthorType.Customer, AuthorType.Companion];

  public get isDisabledState(): boolean {
    return [UserInteractionOutcome.InAfterCallWork, UserInteractionOutcome.InExtendedAfterCallWork, UserInteractionOutcome.Parked].includes(
      this.taskState,
    );
  }

  public get isMediaDownloadDisabled(): boolean {
    return this.taskState === UserInteractionOutcome.Parked;
  }

  public expandedMessages: { [id: string]: boolean } = {};
  public isLoadingPrevMessages = false;
  public hasLoadedAllPrevMessages = false;

  public expandedMedia: IChatMessageMedia | null = null;

  // Setter (not ngAfterViewInit) re-attaches the listener whenever the @if-gated container is torn down and
  // recreated. Attached outside Angular's zone so scrolling doesn't trigger a change-detection tick per event.
  @ViewChild('chatMessagesContainer')
  public set chatMessagesContainer(container: ElementRef<HTMLDivElement>) {
    const element = container?.nativeElement;
    if (element === this.containerRef?.nativeElement) {
      return;
    }
    this.containerRef?.nativeElement.removeEventListener('scroll', this.onScroll);
    this.containerRef = container;
    // A fresh container has no scroll history of its own, so the previous node's state must not leak in.
    this.lastObservedScrollTop = 0;
    this.isNearBottom = true;
    if (element) {
      this.ngZone.runOutsideAngular(() => element.addEventListener('scroll', this.onScroll));
    }
  }

  public get chatMessagesContainer(): ElementRef<HTMLDivElement> {
    return this.containerRef;
  }

  public constructor(
    private readonly store: Store,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly fileService: FileService,
    private readonly ngZone: NgZone,
    private readonly injector: Injector,
  ) {}

  @HostListener('document:keydown.escape')
  public onEscapeKey(): void {
    this.closeMediaPreview();
  }

  public ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  public ngOnDestroy(): void {
    this.containerRef?.nativeElement.removeEventListener('scroll', this.onScroll);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const prev = changes.messages?.previousValue;
    const curr = changes.messages?.currentValue;
    const lastMessageChanged = prev?.length > 0 && curr?.length > 0 && prev[prev.length - 1]?.content !== curr[curr.length - 1]?.content;
    if (
      changes.messages &&
      curr &&
      (this.isLoadingPrevMessages || // always process when finishing a prev-load
        changes.messages.firstChange || // process initial render
        prev?.length !== curr.length || // real add/remove, ignore status updates
        lastMessageChanged) // typing indicator replaced by actual response
    ) {
      if (this.isLoadingPrevMessages) {
        this.isLoadingPrevMessages = false;
        this.hasLoadedAllPrevMessages = !this.hasMoreMessages;

        const container = this.chatMessagesContainer?.nativeElement;
        const scrollHeightWithSpinner = container?.scrollHeight ?? 0;
        const scrollTopWithSpinner = container?.scrollTop ?? 0;

        of({})
          .pipe(delay(0), untilDestroyed(this))
          .subscribe(() => {
            if (container) {
              const scrollDelta = container.scrollHeight - scrollHeightWithSpinner;
              container.scrollTop = scrollTopWithSpinner + scrollDelta;
              this.lastObservedScrollTop = container.scrollTop;
            }
            this.copyTextInitialized.emit(container?.innerText);
          });
      } else {
        if (!this.hasLoadedAllPrevMessages) {
          const limit = INITIAL_MESSAGES_LIMIT_MAPPER[this.chatType];
          this.hasLoadedAllPrevMessages = !this.hasMoreMessages && (!limit || changes.messages.currentValue.length < limit);
        }

        of({})
          .pipe(delay(0), untilDestroyed(this))
          .subscribe(() => {
            this.scrollToBottom();
            this.copyTextInitialized.emit(this.chatMessagesContainer?.nativeElement?.innerText);
          });
      }
    }
  }

  public sendMessageAgain(message: IChatMessage): void {
    this.messageSent.emit(message.content);
    message.hasBeenResent = true;
  }

  public isMessageSentFailed(status: ChatMessageStatus): boolean {
    return status === TwilioMessageStatus.Failed;
  }

  public isMessageUndelivered(status: ChatMessageStatus): boolean {
    return status === TwilioMessageStatus.Undelivered;
  }

  public getAttachmentNotSentMessage(message: IChatMessage): string {
    return getMessageDeliveryFailureReasonMessage(message.failureReason, !!message.media?.length, this.translateService);
  }

  public onScrolledUp(): void {
    if (this.hasLoadedAllPrevMessages || this.isLoadingPrevMessages) {
      return;
    }
    const el = this.chatMessagesContainer?.nativeElement;
    if (!el) {
      return;
    }
    this.isLoadingPrevMessages = true;
    this.loadPrevMessagesRequested.emit();
  }

  public toggleExpand(id: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.expandedMessages[id] = !this.expandedMessages[id];
  }

  public getAuthorIcon(authorType: AuthorType): Icon {
    switch (authorType) {
      case AuthorType.Customer:
        return ICONS.OPERATOR_CUSTOMER;
      case AuthorType.Agent:
        return ICONS.USER_LIGHT;
      case AuthorType.Companion:
        return ICONS.COMPANION_LIGHT;
      case AuthorType.System:
        return ICONS.VIRTUAL_USER_LIGHT;
    }
  }

  public isBlankFormatLine(line: IChatInlineNode[]): boolean {
    return line.length === 1 && line[0].type === 'text' && line[0].value === '';
  }

  public getStatusIcon(status: ChatMessageStatus): Icon {
    if (this.chatType === ChatServiceType.WebChat) {
      return null;
    }

    switch (status) {
      case TwilioMessageStatus.Sent:
      case TwilioMessageStatus.Read:
      case TwilioMessageStatus.Delivered:
        return ICONS.CHECK;
      case TwilioMessageStatus.Pending:
        return ICONS.CLOCK;
      case TwilioMessageStatus.Failed:
      case TwilioMessageStatus.Undelivered:
        return ICONS.EXCLAMATION_CIRCLE_LIGHT;
      default:
        return null;
    }
  }

  public isMessageRead(status: ChatMessageStatus): boolean {
    return (
      ([ChatServiceType.Whatsapp, ChatServiceType.Rcs].includes(this.chatType) && [TwilioMessageStatus.Read].includes(status)) ||
      ([ChatServiceType.Sms].includes(this.chatType) && [TwilioMessageStatus.Delivered].includes(status))
    );
  }

  public showSecondCheckIcon(status: ChatMessageStatus): boolean {
    return (
      [ChatServiceType.Whatsapp, ChatServiceType.Rcs].includes(this.chatType) &&
      [TwilioMessageStatus.Delivered, TwilioMessageStatus.Read].includes(status)
    );
  }

  public openMediaPreview(media: IChatMessageMedia): void {
    this.expandedMedia = media;
  }

  public closeMediaPreview(): void {
    this.expandedMedia = null;
  }

  public downloadMedia(media: IChatMessageMedia): void {
    fetch(media.url)
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = media.filename ?? 'file';
        anchor.click();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => this.toastrService.error(this.translateService.instant(TranslationKeys.GeneralError)));
  }

  public getMediaIcon(media: IChatMessageMedia): string {
    const theme = this.store.selectSnapshot(CommonThemeState.theme);
    const themeFolder = theme === ChartTheme.Dark ? 'dark' : 'light';
    return getChatAttachmentIcon(media.contentType, media.filename, themeFolder);
  }

  public getMediaSize(media: IChatMessageMedia): string {
    return this.fileService.formatFileSize(media.size ?? 0);
  }

  // File tiles render the timestamp on their own size line so it lines up with the download button;
  // every other message keeps it as the trailing element of the message body.
  public isMetadataInsideTile(message: IChatMessage): boolean {
    const hasCaption = !!message.content && !message.isTypingContent;
    if (message.authorType === AuthorType.System || hasCaption || !message.media?.length) {
      return false;
    }
    const lastIndex = message.media.length - 1;
    const lastMedia = message.media[lastIndex];

    return (
      lastMedia.blockReason !== MediaBlockReason.None ||
      this.hasMediaError(`${message.id}_${lastIndex}`) ||
      lastMedia.kind === ChatMediaKind.File ||
      lastMedia.kind === ChatMediaKind.Audio
    );
  }

  public getBlockedTooltip(media: IChatMessageMedia): string {
    return getMediaBlockReasonMessage(media.blockReason, this.translateService);
  }

  public onCopied(): void {
    this.toastrService.success(this.translateService.instant(TranslationKeys.GeneralCopied));
  }

  public onMediaError(mediaKey: string): void {
    this.mediaErrorKeys.add(mediaKey);
    this.changeDetectorRef.markForCheck();
  }

  public retryMedia(mediaKey: string, event: Event): void {
    event.stopPropagation();
    this.mediaErrorKeys.delete(mediaKey);
    this.changeDetectorRef.markForCheck();
  }

  public hasMediaError(mediaKey: string): boolean {
    return this.mediaErrorKeys.has(mediaKey);
  }

  public onAudioValidChanged(mediaKey: string, isValid: boolean): void {
    if (isValid) {
      // isValidChanged fires synchronously, before the player's own OnPush view swaps its spinner for the
      // loaded player - wait for the next render so onMediaLoaded() measures the settled DOM.
      afterNextRender(() => this.onMediaLoaded(), { injector: this.injector });
    } else {
      this.onMediaError(mediaKey);
    }
  }

  // Updates isNearBottom from real scroll events only, so unrelated height changes (toggleExpand, a media
  // error/retry swap) can't corrupt it. Bound field so the same reference works for add/removeEventListener.
  public onScroll = (): void => {
    const container = this.chatMessagesContainer?.nativeElement;
    if (!container) {
      return;
    }
    if (container.scrollTop === this.lastObservedScrollTop) {
      return;
    }
    this.lastObservedScrollTop = container.scrollTop;
    this.isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < this.scrollBottomThreshold;
  };

  // Images/videos/audio have no reserved height until they load, so rows grow taller after we've already
  // scrolled. Re-pin only if the user was already following the conversation at the bottom.
  public onMediaLoaded(): void {
    if (this.isNearBottom) {
      this.scrollToBottom();
    }
  }

  // Jumps instead of animating, so every measurement above reads a settled scroll position.
  private scrollToBottom(): void {
    const container = this.chatMessagesContainer?.nativeElement;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
    this.lastObservedScrollTop = container.scrollTop;
    this.isNearBottom = true;
  }
}
